import type { Invoice } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatDate } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { dayDate, ibanBlocks, periodEnd } from "./subscription";

interface Props {
  invoice: Invoice;
  onDownload: () => void;
  onClose: () => void;
}

/** Text with line breaks between the parts that are there. */
function Lines({ lines }: { lines: (string | null)[] }) {
  return (
    <>
      {lines
        .filter((line): line is string => line !== null && line !== "")
        .map((line) => (
          <span key={line}>
            <br />
            {line}
          </span>
        ))}
    </>
  );
}

/** An invoice or credit note of Tafo, as in the prototype (invoiceHtml), with the PDF to download. */
export function InvoiceDialog({ invoice, onDownload, onClose }: Props) {
  const text = t.subscription.invoice;
  const credit = invoice.kind === "credit_note";
  const sign = credit ? "− " : "";
  const { seller, buyer } = invoice;
  const amount = (cents: number) => sign + formatEuro(cents);
  return (
    <Dialog
      title={`${credit ? text.creditNote : text.invoice} ${invoice.number}`}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {text.close}
          </button>
          <button type="button" className="btn primary" onClick={onDownload}>
            {text.download}
          </button>
        </>
      }
    >
      <div className="inv">
        <div className="inv-top">
          <div>
            <img src="/tafo-logo.png" alt="Tafo" className="inv-logo" />
            <div className="inv-from">
              <b>{seller.name}</b>
              <Lines
                lines={[
                  seller.address,
                  seller.vatNumber && text.vat(seller.vatNumber),
                  seller.iban && text.iban(ibanBlocks(seller.iban)),
                ]}
              />
            </div>
          </div>
          <div className="inv-meta">
            <h2>{credit ? text.creditNote : text.invoice}</h2>
            <div>
              <span>{text.number}</span>
              <b>{invoice.number}</b>
            </div>
            <div>
              <span>{text.date}</span>
              <b>{formatDate(invoice.issuedAt)}</b>
            </div>
            {credit ? (
              <div>
                <span>{text.forInvoice}</span>
                <b>{invoice.credits}</b>
              </div>
            ) : (
              <div>
                <span>{text.due}</span>
                <b>{dayDate(invoice.period)}</b>
              </div>
            )}
            <div>
              <span>{text.customer}</span>
              <b>{buyer.customer}</b>
            </div>
          </div>
        </div>
        <div className="inv-to">
          <span>{text.to}</span>
          <b>{buyer.name}</b>
          <Lines lines={[buyer.address, buyer.vatNumber && text.vat(buyer.vatNumber), text.shop(buyer.shop)]} />
        </div>
        <table className="inv-t">
          <thead>
            <tr>
              <th>{text.description}</th>
              <th>{text.period}</th>
              <th className="r">{text.net}</th>
              <th className="r">{text.vatRate(invoice.vatRate)}</th>
              <th className="r">{text.total}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {credit ? text.crediting : ""}
                {invoice.description}
              </td>
              <td>
                {dayDate(invoice.period)} – {dayDate(periodEnd(invoice.period))}
              </td>
              <td className="r">{amount(invoice.netCents)}</td>
              <td className="r">{amount(invoice.vatCents)}</td>
              <td className="r">{amount(invoice.totalCents)}</td>
            </tr>
          </tbody>
        </table>
        <div className="inv-sum">
          <div>
            <span>{text.totalNet}</span>
            <b>{amount(invoice.netCents)}</b>
          </div>
          <div>
            <span>{text.vatRate(invoice.vatRate)}</span>
            <b>{amount(invoice.vatCents)}</b>
          </div>
          <div className="big">
            <span>{text.total}</span>
            <b>{amount(invoice.totalCents)}</b>
          </div>
        </div>
        {credit ? (
          <div className="inv-paid cn">{text.reason(invoice.reason ?? "", invoice.refund)}</div>
        ) : (
          <div className="inv-paid">{text.paid(formatDate(invoice.issuedAt), invoice.paidWith ?? "")}</div>
        )}
        <p className="inv-foot">{text.foot(invoice.structuredReference)}</p>
      </div>
    </Dialog>
  );
}
